//===----------------------------------------------------------------------===//
//
//  Copyright 2024-2026 Trovares Inc. dba Rocketgraph.  All rights reserved.
//
//===----------------------------------------------------------------------===//

import { useEffect, useState } from 'react';
import {
  Alert, Box, Button, CircularProgress, Dialog, DialogActions,
  DialogContent, DialogTitle, IconButton, Tooltip, Typography
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import BrandedTitleBar from '@/components/Elements/BrandedTitleBar';
import { useConfig } from '@/hooks/useConfig';
import {
  type ActivePlugin, type PluginInfoItem, type PluginInfoPayload,
  type PluginJsonSurface
} from '@/services/apiTypes';

interface PluginInfoModalProps {
  plugin: ActivePlugin;
  surface: PluginJsonSurface;
  onClose: () => void;
}

type ModalState =
  | { kind: 'loading' }
  | { kind: 'loaded'; payload: PluginInfoPayload }
  | { kind: 'error'; message: string };

// Coerce non-string values to text so the modal never renders raw objects or
// HTML.  `null` becomes empty string per the contract; numbers and booleans
// use their JSON-style serialization.
const renderValue = (value: PluginInfoItem['value']): string => {
  if (value === null) return '';
  return typeof value === 'string' ? value : String(value);
};

/**
 * Renders the plugin "info" surface payload as titled sections of label/value
 * rows.  The component fetches on mount and unmounts on close — there is no
 * caching because the data is debug info that should reflect *now*.  When the
 * fetch fails (network error, non-2xx, or unsupported schema_version), the
 * modal falls back to manifest data so a single broken plugin can never make
 * the gear menu look broken.
 */
function PluginInfoModal({ plugin, surface, onClose }: PluginInfoModalProps) {
  const { fetchPluginInfo } = useConfig();
  const [state, setState] = useState<ModalState>({ kind: 'loading' });
  // Bumped by the retry button to re-trigger the fetch effect without
  // unmounting / remounting the modal.
  const [reloadCount, setReloadCount] = useState(0);

  const fallbackTitle = surface.label || plugin.name;

  useEffect(() => {
    let cancelled = false;
    setState({ kind: 'loading' });
    fetchPluginInfo(surface.url)
      .then((payload) => {
        if (!cancelled) setState({ kind: 'loaded', payload });
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        const message = err instanceof Error ? err.message : String(err);
        setState({ kind: 'error', message });
      });
    return () => { cancelled = true; };
  }, [fetchPluginInfo, surface.url, reloadCount]);

  const handleRetry = () => setReloadCount((n) => n + 1);

  const titleText =
    state.kind === 'loaded' ?
      (state.payload.title ?? fallbackTitle) :
      fallbackTitle;

  return (
    <Dialog
      open
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      aria-labelledby="plugin-info-modal-title"
    >
      <DialogTitle>
        <BrandedTitleBar
          title={titleText}
          titleId="plugin-info-modal-title"
          actions={(
            <Tooltip describeChild title="Close">
              <span>
                <IconButton onClick={onClose} aria-label="Close plugin info">
                  <CloseIcon />
                </IconButton>
              </span>
            </Tooltip>
          )}
        />
      </DialogTitle>
      <DialogContent dividers>
        {state.kind === 'loading' && (
          <Box sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 2,
            py: 2
          }}>
            <CircularProgress size={20} aria-hidden />
            <Typography>Loading…</Typography>
          </Box>
        )}

        {state.kind === 'loaded' && state.payload.sections.length === 0 && (
          <Typography sx={{ color: 'text.secondary' }}>
            This plugin did not report any details.
          </Typography>
        )}

        {state.kind === 'loaded' && state.payload.sections.map(
          (section, sectionIndex) => (
            <Box
              key={sectionIndex}
              sx={{
                mb: sectionIndex < state.payload.sections.length - 1 ? 2 : 0
              }}
            >
              {section.title && (
                <Typography variant="subtitle2" sx={{ mb: 1 }}>
                  {section.title}
                </Typography>
              )}
              <Box
                component="dl"
                sx={{
                  m: 0,
                  display: 'grid',
                  gridTemplateColumns: 'max-content 1fr',
                  columnGap: 2,
                  rowGap: 0.5
                }}
              >
                {section.items.map((item, itemIndex) => (
                  // Use a fragment per row so the dt/dd land in adjacent grid
                  // cells without an intermediate wrapper that would break the
                  // two-column layout.
                  <Box
                    key={itemIndex}
                    sx={{ display: 'contents' }}
                  >
                    <Typography
                      component="dt"
                      variant="body2"
                      sx={{ color: 'text.secondary', m: 0 }}
                    >
                      {item.label}
                    </Typography>
                    <Typography
                      component="dd"
                      variant="body2"
                      sx={{ m: 0, wordBreak: 'break-word' }}
                    >
                      {renderValue(item.value)}
                    </Typography>
                  </Box>
                ))}
              </Box>
            </Box>
          )
        )}

        {state.kind === 'error' && (
          <Box>
            <Alert severity="warning" sx={{ mb: 2 }}>
              Could not load plugin details.
            </Alert>
            <Box
              component="dl"
              sx={{
                m: 0,
                display: 'grid',
                gridTemplateColumns: 'max-content 1fr',
                columnGap: 2,
                rowGap: 0.5
              }}
            >
              <Box sx={{ display: 'contents' }}>
                <Typography component="dt" variant="body2"
                  sx={{ color: 'text.secondary', m: 0 }}>Name</Typography>
                <Typography component="dd" variant="body2" sx={{ m: 0 }}>
                  {plugin.name}
                </Typography>
              </Box>
              <Box sx={{ display: 'contents' }}>
                <Typography
                  component="dt"
                  variant="body2"
                  sx={{ color: 'text.secondary', m: 0 }}
                >
                  Description
                </Typography>
                <Typography component="dd" variant="body2" sx={{ m: 0 }}>
                  {plugin.description}
                </Typography>
              </Box>
              <Box sx={{ display: 'contents' }}>
                <Typography component="dt" variant="body2"
                  sx={{ color: 'text.secondary', m: 0 }}>Mount</Typography>
                <Typography component="dd" variant="body2" sx={{ m: 0 }}>
                  {plugin.mount}
                </Typography>
              </Box>
              <Box sx={{ display: 'contents' }}>
                <Typography
                  component="dt"
                  variant="body2"
                  sx={{ color: 'text.secondary', m: 0 }}
                >
                  API version
                </Typography>
                <Typography component="dd" variant="body2" sx={{ m: 0 }}>
                  {plugin.plugin_api_version}
                </Typography>
              </Box>
            </Box>
          </Box>
        )}
      </DialogContent>
      {/* Only ever Retry. Dismissal is the corner control alone -- a second
          one that did the same thing is what a panel's title bar was cut
          down to avoid -- so with nothing to retry there is no action bar. */}
      {state.kind === 'error' && (
        <DialogActions>
          <Button onClick={handleRetry}>Retry</Button>
        </DialogActions>
      )}
    </Dialog>
  );
}

export default PluginInfoModal;
